// Application Components
import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { object, string } from 'yup'
import { translateField } from '../../functions/field-functions'
import {
    subtractValuesAdditionalConditionalForm,
    extractSubmissionForm,
} from '../../functions/form-functions'

//// Application Slices
import { selectSettings } from '../../reducers/settingSlice'
import {
    updateFormData,
    updateFormValidity,
    deleteFormData,
    updateFormSystem,
    selectForms,
} from '../../reducers/formSlice'

//// Application Components
import DividerComponent from '../../components/simple-components/divider-component'
import ButtonComponent from '../../components/conditional-components/button-component'
import LoadingComponent from '../../components/simple-components/loading-component'
import NotificationComponent from '../../components/conditional-components/notification-component'
import FileUploadComponent from '../../components/file-upload-components/file-upload-component'
import AdditionalCriminalAndMedicaidMedicareFraudQuestionsConvictedGuiltyChapterQuestions from '../../components/generic-components/additional-criminal-and-medicaid-medicare-fraud-questions-convicted-guilty-chapter-questions-component'
import AdditionalCriminalAndMedicaidMedicareFraudQuestionsConvictedGuiltyMedicareMedicaid from '../../components/generic-components/additional-criminal-and-medicaid-medicare-fraud-questions-convicted-guilty-medicare-medicaid-component'
import AdditionalCriminalAndMedicaidMedicareFraudTerminatedFloridaQuestions from '../../components/generic-components/additional-criminal-and-medicaid-medicare-fraud-terminated-florida-questions-component'
import AdditionalCriminalAndMedicaidMedicareFraudTerminatedOtherStateQuestions from '../../components/generic-components/additional-criminal-and-medicaid-medicare-fraud-terminated-other-state-questions-component'
import AdditionalCriminalAndMedicaidMedicareFraudExcludedIndividualsAndEntitiesQuestions from '../../components/generic-components/additional-criminal-and-medicaid-medicare-fraud-excluded-individuals-and-entities-questions-component'

//// Carbon Components
import { Form, RadioButtonGroup, RadioButton } from '@carbon/react'

//// Carbon Icons

//// Static Constants

//////// Component
const CriminalAndMedicaidMedicareFraudQuestionsForm = ({ instance }) => {
    //////// Hooks
    const dispatch = useDispatch()
    const navigate = useNavigate()

    //////// State
    const [loading, setLoading] = useState(false)
    const [notification, setNotification] = useState({})

    //////// Slices
    const settingsData = useSelector(selectSettings)
    const formsData = useSelector(selectForms)

    //// Slice Data
    const digital_name = settingsData?.['form']?.['digital_name']
    const submit_route = settingsData?.['form']?.['submit_route']
    const fields = settingsData?.['form']?.['fields']
    const conditions = settingsData?.['form']?.['conditions']

    //////// Search Params

    //////// Methods

    // Generate Validation Schema
    const generateStaticSchema = async () => {
        try {
            const dynamic = {}
            dynamic.addtObjs = [{}]
            dynamic.addtFilds = [{}]

            // Additional Questions 1 (One)
            if (formsData?.[digital_name]?.values?.FELVIO === 'Y') {
                dynamic.addtObjs[0].FELVIO1A = ''
                dynamic.addtObjs[0].FELVIO1B = ''
                dynamic.addtObjs[0].FELVIO1C = ''
                dynamic.addtObjs[0].FELVIO1D = ''
                dynamic.addtFilds[0].FELVIO1AErrMsg = ''
                dynamic.addtFilds[0].FELVIO1BErrMsg = ''
                dynamic.addtFilds[0].FELVIO1CErrMsg = ''
                dynamic.addtFilds[0].FELVIO1DErrMsg = ''
            }

            // Additional Questions 2 (Two)s
            if (formsData?.[digital_name]?.values?.FEDFEL === 'Y') {
                dynamic.addtObjs[0].FEDFEL2A = ''
                dynamic.addtFilds[0].FEDFEL2A = false
                dynamic.addtFilds[0].FEDFEL2AErrMsg = ''
            }

            // Additional Questions 3 (Three)
            if (formsData?.[digital_name]?.values?.TRMFLMED === 'Y') {
                dynamic.addtObjs[0].TRMFLMED3A = ''
                dynamic.addtFilds[0].TRMFLMED3A = false
                dynamic.addtFilds[0].TRMFLMED3AErrMsg = ''
            }

            // Additional Questions 4 (Four)
            if (formsData?.[digital_name]?.values?.TRMOSMED === 'Y') {
                dynamic.addtObjs[0].TRMOSMED4A = ''
                dynamic.addtObjs[0].TRMOSMED4B = ''
                dynamic.addtFilds[0].TRMOSMED4A = false
                dynamic.addtFilds[0].TRMOSMED4B = false
                dynamic.addtFilds[0].TRMOSMED4AErrMsg = ''
                dynamic.addtFilds[0].TRMOSMED4BErrMsg = ''
            }

            // Additional Questions 5 (Five)
            if (formsData?.[digital_name]?.values?.LEIE === 'Y') {
                dynamic.addtObjs[0].LEIE2A = ''
                dynamic.addtFilds[0].LEIE2A = false
                dynamic.addtFilds[0].LEIE2AErrMsg = ''
            }

            // Additional Questions 5a (Fivea)
            if (formsData?.[digital_name]?.values?.LEIE2A === 'Y') {
                dynamic.addtObjs[0].LEIE2B = ''
                dynamic.addtFilds[0].LEIE2B = false
                dynamic.addtFilds[0].LEIE2BErrMsg = ''
            }

            dynamic.addtObjs[0] = {
                ...dynamic.addtObjs[0],
                FELVIO: '',
                FEDFEL: '',
                TRMFLMED: '',
                TRMOSMED: '',
                LEIE: '',
            }
            dynamic.addtFilds[0] = {
                ...dynamic.addtFilds[0],
                FELVIO: false,
                FELVIOErrMsg: '',
                FEDFEL: false,
                FEDFELErrMsg: '',
                TRMFLMED: false,
                TRMFLMEDErrMsg: '',
                TRMOSMED: false,
                TRMOSMEDErrMsg: '',
                LEIE: false,
                LEIEErrMsg: '',
            }
            dynamic.addtObjsSchema = {}
            if (dynamic?.addtObjs?.length > 0) {
                dynamic.addtObjs.forEach(async (el) => {
                    dynamic.addtObjsSchema = {
                        ...dynamic.addtObjsSchema,
                        ...el,
                    }
                })
            }
            dynamic.addtFildsSchema = {}
            if (dynamic?.addtFilds?.length > 0) {
                dynamic.addtFilds.forEach(async (el) => {
                    dynamic.addtFildsSchema = {
                        ...dynamic.addtFildsSchema,
                        ...el,
                    }
                })
            }
            dynamic.addtObjsSchemaKeys = Object?.keys(dynamic.addtObjsSchema)
            dynamic.addtFildsSchemaKeys = Object?.keys(dynamic.addtFildsSchema)
            dynamic.addtSchema = {}
            if (dynamic?.addtObjsSchemaKeys?.length > 0) {
                dynamic.addtObjsSchemaKeys.forEach(async (el) => {
                    dynamic.addtSchema = {
                        ...dynamic.addtSchema,
                        [el]: string().required(
                            'This field is required to be entered or selected.'
                        ),
                    }
                })
            }
            dynamic.addtValid = {}
            if (dynamic?.addtFildsSchemaKeys?.length > 0) {
                dynamic.addtFildsSchemaKeys.forEach(async (el) => {
                    if (el?.includes('ErrMsg')) {
                        dynamic.addtValid = {
                            ...dynamic.addtValid,
                            [el]: '',
                        }
                    } else {
                        dynamic.addtValid = {
                            ...dynamic.addtValid,
                            [el]: false,
                        }
                    }
                })
            }
            return dynamic
        } catch (error) {
            console.warn('Generate Static Schema Error:', error)
            return null
        }
    }

    // Validate Data Submission
    const validation = async (submit) => {
        // Validate Form Submission Data Against Schema
        try {
            // Schema Object
            const schema = {}
            if (
                (formsData?.[digital_name]?.values?.FELVIO === undefined ||
                    formsData?.[digital_name]?.values?.FELVIO === 'N') &&
                (formsData?.[digital_name]?.values?.FEDFEL === undefined ||
                    formsData?.[digital_name]?.values?.FEDFEL === 'N') &&
                (formsData?.[digital_name]?.values?.TRMFLMED === undefined ||
                    formsData?.[digital_name]?.values?.TRMFLMED === 'N') &&
                (formsData?.[digital_name]?.values?.TRMOSMED === undefined ||
                    formsData?.[digital_name]?.values?.TRMOSMED === 'N') &&
                (formsData?.[digital_name]?.values?.LEIE === undefined ||
                    formsData?.[digital_name]?.values?.LEIE === 'N')
            ) {
                schema.yup = object({
                    FELVIO: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    FEDFEL: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    TRMFLMED: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    TRMOSMED: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    LEIE: string().required(
                        'This field is required to be entered or selected.'
                    ),
                })
                schema.valid = {
                    FELVIO: false,
                    FELVIOErrMsg: '',
                    FEDFEL: false,
                    FEDFELErrMsg: '',
                    TRMFLMED: false,
                    TRMFLMEDErrMsg: '',
                    TRMOSMED: false,
                    TRMOSMEDErrMsg: '',
                    LEIE: false,
                    LEIEErrMsg: '',
                }
            } else {
                const { addtSchema, addtValid } = await generateStaticSchema()
                schema.yup = object().shape({ ...addtSchema })
                schema.valid = addtValid
            }
            await schema.yup.validate(
                { ...submit },
                { strict: true, abortEarly: false }
            )
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: {},
                })
            )
            return true
        } catch (err) {
            const errors = {}
            if (err?.inner?.length > 0) {
                await err.inner.forEach(async (el) => {
                    if (el?.path) {
                        errors[el?.path] = true
                        if (el?.errors?.length > 0) {
                            const errName = el?.path + 'ErrMsg'
                            const errMsg = el?.errors?.join(' ')
                            const tran = translateField(fields, el?.path)
                            if (tran) {
                                const tranErrMsg = await errMsg.replaceAll(
                                    el.path,
                                    tran
                                )
                                errors[errName] = tranErrMsg
                            } else {
                                errors[errName] = el?.errors?.join(' ')
                            }
                        }
                    }
                })
            }
            //// Start Watson Assistant Journey

            //// End Watson Assistant Journey
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: errors,
                })
            )
            return false
        }
    }

    // Re-Validate Data Submission
    const revalidation = async (event_name) => {
        try {
            // Update Validation State
            if (digital_name && event_name) {
                dispatch(
                    updateFormValidity({
                        digital_name: digital_name,
                        errors: {
                            [event_name]: false,
                            [event_name + 'ErrMsg']: '',
                        },
                    })
                )
            }
        } catch (error) {
            console.warn('Revalidation Error:', error)
            return null
        }
    }

    //////// Functional Methods

    // Handle Removal Of Additional Other State License Component/s
    const handleSubtractAdditionalConditionalComponent = (e) => {
        try {
            if (e?.target?.name && e?.target?.value) {
                const values = subtractValuesAdditionalConditionalForm(
                    conditions,
                    formsData?.[digital_name]?.values,
                    e?.target?.name,
                    e?.target?.value
                )
                if (values) {
                    dispatch(
                        deleteFormData({
                            digital_name: digital_name,
                        })
                    )
                    dispatch(
                        updateFormData({
                            digital_name: digital_name,
                            values: values,
                        })
                    )
                }
            }
        } catch (error) {
            console.warn(
                'Handle Subtract Additional Other State Licenses Component Error:',
                error
            )
            return null
        }
    }

    // Handle Data Of All Fields
    const handleData = (e) => {
        if (e?.target?.name && e?.target?.value) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: {
                        [e.target.name]: e.target.value,
                    },
                })
            )
            revalidation(e.target.name)
        } else if (e?.target?.name) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: { [e.target.name]: '' },
                })
            )
            revalidation(e.target.name)
        }
        //// Start Watson Assistant Journey

        if (
            (e?.target?.name === 'FELVIO' && e?.target?.value === 'Y') ||
            (e?.target?.name === 'FEDFEL' && e?.target?.value === 'Y') ||
            (e?.target?.name === 'TRMFLMED' && e?.target?.value === 'Y') ||
            (e?.target?.name === 'TRMOSMED' && e?.target?.value === 'Y') ||
            (e?.target?.name === 'LEIE' && e?.target?.value === 'Y')
        ) {
            instance.tours.startTour(
                'Registered Nursing Endorsement Medicaid Medicare Fraud Questions Yes Tour'
            )
        } else if (e?.target?.name === 'LEIE' && e?.target?.value === 'N') {
            instance.tours.startTour(
                'Registered Nursing Endorsement Medicaid Medicare Fraud Questions LEIE No Tour'
            )
        } else {
            instance.tours.endTour()
        }

        //// End Watson Assistant Journey
    }

    // Handle Form Submission
    const handleSubmit = async () => {
        try {
            console.info(
                'Criminal And Medicaid Medicare Fraud Questions: Submitted'
            )

            setLoading(true)

            // Extract Form Data From Redux
            const submit = await extractSubmissionForm(digital_name, formsData)

            // Validate Form Submission Data
            const valid = await validation(submit)

            // Check If Form Data Is Valid
            if (valid) {
                console.info(
                    'Criminal And Medicaid Medicare Fraud Questions: Passed Valid'
                )

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: true },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: false,
                    kind: '',
                    title: '',
                    subtitle: '',
                })

                // Navigate To Next Form
                navigate(submit_route ? submit_route : '/')
            } else {
                console.info(
                    'Criminal And Medicaid Medicare Fraud Questions: Failed Valid'
                )

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: false },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: true,
                    kind: 'error',
                    title: 'Form Errors:',
                    subtitle:
                        'There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.',
                })

                console.log(
                    'Criminal And Medicaid Medicare Fraud Questions Failed Valid: There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.'
                )
            }
        } catch (error) {
            console.warn('Handle Submit Error:', error)
            return null
        }
    }

    // Handle Keyboard Enter As Submit (Method)
    const handleKeyDown = async (event) => {
        try {
            if (event?.key === 'Enter') {
                await handleSubmit()
            }
        } catch (error) {
            console.warn('Handle Down Key Error:', error)
            return null
        }
    }

    //////// Memoization

    //////// Effects/Side Effects

    // Reload Watson Assistant Journey on Reload
    useEffect(() => {
        if (instance?.tours) {
            instance.tours.endTour()
        }
        return () => {}
    }, [instance])

    //////////////// Render & Return

    if (loading) {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <LoadingComponent />
                </Form>
            </>
        )
    } else {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <p>
                        <strong>IMPORTANT NOTICE:</strong> Applicants for
                        licensure, certification, or registration and candidates
                        for examination may be excluded from licensure,
                        certification, or registration if their felony
                        convictions fall into certain timeframes as established
                        in section 456.0635(2), Florida Statutes.
                    </p>

                    <DividerComponent text="Question 1" />

                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.FELVIO
                                ? 'required invalid'
                                : 'required'
                        }
                        defaultSelected={
                            formsData?.[digital_name]?.values?.FELVIO
                        }
                        valueSelected={
                            formsData?.[digital_name]?.values?.FELVIO
                        }
                        legendText="Have you been convicted of, or entered a plea of guilty or nolo contendere, regardless of adjudication, a felony under Chapter 409, Florida Statutes (relating to social and economic assistance), Chapter 817, Florida Statutes (relating to fraudulent practices), Chapter 893, Florida Statutes (relating to drug abuse prevention and control), or a similar felony offense(s) in another state or jurisdiction?"
                        name="FELVIO"
                        invalid={formsData?.[digital_name]?.errors?.FELVIO}
                        invalidText={
                            formsData?.[digital_name]?.errors?.FELVIOErrMsg
                        }
                    >
                        <RadioButton
                            id="FELVIOTrue"
                            name="FELVIOTrue"
                            labelText="Yes"
                            value="Y"
                            type="radio"
                            onClick={(e) => {
                                handleData(e)
                                handleSubtractAdditionalConditionalComponent(e)
                            }}
                        />
                        <RadioButton
                            id="FELVIOFalse"
                            name="FELVIOFalse"
                            labelText="No"
                            value="N"
                            type="radio"
                            onClick={(e) => {
                                handleData(e)
                                handleSubtractAdditionalConditionalComponent(e)
                            }}
                        />
                    </RadioButtonGroup>

                    <AdditionalCriminalAndMedicaidMedicareFraudQuestionsConvictedGuiltyChapterQuestions />

                    <DividerComponent text="Question 2" />

                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.FEDFEL
                                ? 'required invalid'
                                : 'required'
                        }
                        defaultSelected={
                            formsData?.[digital_name]?.values?.FEDFEL
                        }
                        valueSelected={
                            formsData?.[digital_name]?.values?.FEDFEL
                        }
                        legendText="Have you been convicted of, or entered a plea of guilty or nolo contendere to, regardless of adjudication, to a felony under 21 U.S.C. ss. 801-970 or 42 U.S.C. ss. 1395-1396 (relating to public health, welfare, Medicare and Medicaid issues)?"
                        name="FEDFEL"
                        invalid={formsData?.[digital_name]?.errors?.FEDFEL}
                        invalidText={
                            formsData?.[digital_name]?.errors?.FEDFELErrMsg
                        }
                    >
                        <RadioButton
                            id="FEDFELTrue"
                            name="FEDFELTrue"
                            labelText="Yes"
                            value="Y"
                            type="radio"
                            onClick={(e) => {
                                handleData(e)
                                handleSubtractAdditionalConditionalComponent(e)
                            }}
                        />
                        <RadioButton
                            id="FEDFELFalse"
                            name="FEDFELFalse"
                            labelText="No"
                            value="N"
                            type="radio"
                            onClick={(e) => {
                                handleData(e)
                                handleSubtractAdditionalConditionalComponent(e)
                            }}
                        />
                    </RadioButtonGroup>

                    <AdditionalCriminalAndMedicaidMedicareFraudQuestionsConvictedGuiltyMedicareMedicaid />

                    <DividerComponent text="Question 3" />

                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.TRMFLMED
                                ? 'required invalid'
                                : 'required'
                        }
                        defaultSelected={
                            formsData?.[digital_name]?.values?.TRMFLMED
                        }
                        valueSelected={
                            formsData?.[digital_name]?.values?.TRMFLMED
                        }
                        legendText="Have you ever been terminated for cause from the Florida Medicaid Program pursuant to s. 409.913, Florida Statutes?"
                        name="TRMFLMED"
                        invalid={formsData?.[digital_name]?.errors?.TRMFLMED}
                        invalidText={
                            formsData?.[digital_name]?.errors?.TRMFLMEDErrMsg
                        }
                    >
                        <RadioButton
                            id="TRMFLMEDTrue"
                            name="TRMFLMEDTrue"
                            labelText="Yes"
                            value="Y"
                            type="radio"
                            onClick={(e) => {
                                handleData(e)
                                handleSubtractAdditionalConditionalComponent(e)
                            }}
                        />
                        <RadioButton
                            id="TRMFLMEDFalse"
                            name="TRMFLMEDFalse"
                            labelText="No"
                            value="N"
                            type="radio"
                            onClick={(e) => {
                                handleData(e)
                                handleSubtractAdditionalConditionalComponent(e)
                            }}
                        />
                    </RadioButtonGroup>

                    <AdditionalCriminalAndMedicaidMedicareFraudTerminatedFloridaQuestions />

                    <DividerComponent text="Question 4" />

                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.TRMOSMED
                                ? 'required invalid'
                                : 'required'
                        }
                        defaultSelected={
                            formsData?.[digital_name]?.values?.TRMOSMED
                        }
                        valueSelected={
                            formsData?.[digital_name]?.values?.TRMOSMED
                        }
                        legendText="Have you ever been terminated for cause, pursuant to the appeals procedures established by the state, from any other state Medicaid program?"
                        name="TRMOSMED"
                        invalid={formsData?.[digital_name]?.errors?.TRMOSMED}
                        invalidText={
                            formsData?.[digital_name]?.errors?.TRMOSMEDErrMsg
                        }
                    >
                        <RadioButton
                            id="TRMOSMEDTrue"
                            name="TRMOSMEDTrue"
                            labelText="Yes"
                            value="Y"
                            type="radio"
                            onClick={(e) => {
                                handleData(e)
                                handleSubtractAdditionalConditionalComponent(e)
                            }}
                        />
                        <RadioButton
                            id="TRMOSMEDFalse"
                            name="TRMOSMEDFalse"
                            labelText="No"
                            value="N"
                            type="radio"
                            onClick={(e) => {
                                handleData(e)
                                handleSubtractAdditionalConditionalComponent(e)
                            }}
                        />
                    </RadioButtonGroup>

                    <AdditionalCriminalAndMedicaidMedicareFraudTerminatedOtherStateQuestions />

                    <DividerComponent text="Question 5" />

                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.LEIE
                                ? 'required invalid'
                                : 'required'
                        }
                        defaultSelected={
                            formsData?.[digital_name]?.values?.LEIE
                        }
                        valueSelected={formsData?.[digital_name]?.values?.LEIE}
                        legendText="Are you currently listed on the United States Department of Health and Human Services Office of Inspector General's List of Excluded Individuals and Entities (LEIE)?"
                        name="LEIE"
                        invalid={formsData?.[digital_name]?.errors?.LEIE}
                        invalidText={
                            formsData?.[digital_name]?.errors?.LEIEErrMsg
                        }
                    >
                        <RadioButton
                            id="LEIETrue"
                            name="LEIETrue"
                            labelText="Yes"
                            value="Y"
                            type="radio"
                            onClick={(e) => {
                                handleData(e)
                                handleSubtractAdditionalConditionalComponent(e)
                            }}
                        />
                        <RadioButton
                            id="LEIEFalse"
                            name="LEIEFalse"
                            labelText="No"
                            value="N"
                            type="radio"
                            onClick={(e) => {
                                handleData(e)
                                handleSubtractAdditionalConditionalComponent(e)
                            }}
                        />
                    </RadioButtonGroup>

                    <AdditionalCriminalAndMedicaidMedicareFraudExcludedIndividualsAndEntitiesQuestions />

                    <div className="file-upload-group">
                        <div className="file-upload-group-item">
                            <FileUploadComponent />
                        </div>
                    </div>

                    <NotificationComponent notification={notification} />

                    <div className="button-group">
                        <div className="button-group-item">
                            <ButtonComponent
                                click={handleSubmit}
                                title="Save Progress"
                            />
                        </div>
                    </div>
                </Form>
            </>
        )
    }
}

export default CriminalAndMedicaidMedicareFraudQuestionsForm
